import { useClient, useDeleteInboxNotification } from "@liveblocks/react";
import { useCallback, useMemo } from "react";

/**
 * Client-side deletion of $toolConfirmation inbox notifications.
 *
 * Tool-confirmation notifications are ephemeral nudges: once the user has dealt
 * with a chat's pending approvals — by confirming, rejecting, or sending a new
 * message (which auto-rejects anything still pending) — the bell item is noise
 * and should disappear. We can't delete server-side (triggerInboxNotification
 * returns no id, and the notification webhook is delayed and uncorrelatable), so
 * the client looks the notification up and deletes it.
 *
 * Deletion is on-demand: each deleteForSession call fetches the current
 * $toolConfirmation notifications straight from the server (no standing
 * subscription) and deletes the matches through useDeleteInboxNotification, so
 * the Liveblocks store gets its built-in optimistic update and the bell clears
 * instantly. The backend may create the notification *after* the user has
 * already resolved the confirmations, so the lookup retries on a fixed schedule
 * — the retry loop is module-level and survives component unmount.
 */

// Absolute offsets from the first attempt for one deletion request. The first
// attempt is immediate; the rest cover a notification that the backend creates
// only after the confirmation was resolved (webhook-ish delay, usually
// seconds). The loop sleeps the delta between offsets so attempts land at these
// times, not their cumulative sum.
const RETRY_OFFSETS_MS = [0, 5_000, 20_000, 60_000];

// Sessions with a deletion request in flight, mapped to the latest request
// time. A second request for the same session doesn't start a second loop; it
// just bumps the timestamp, which the running loop re-reads on each attempt.
const pendingDeletionsBySession = new Map<string, number>();

// Matches useClient()'s return type exactly (Client<BaseUserMeta>); the
// broader default Client type is not assignable from it.
type LiveblocksClient = ReturnType<typeof useClient>;

type DeleteInboxNotificationFn = (inboxNotificationId: string) => void;

/**
 * Returns { deleteForSession } for removing a chat's $toolConfirmation
 * notifications. deleteForSession is fire-and-forget and referentially stable
 * across renders.
 */
export function useToolConfirmationNotifications() {
  const client = useClient();
  const deleteInboxNotification = useDeleteInboxNotification();

  const deleteForSession = useCallback(
    (flowSessionId: string) => {
      if (!flowSessionId) return;
      const alreadyRunning = pendingDeletionsBySession.has(flowSessionId);
      pendingDeletionsBySession.set(flowSessionId, Date.now());
      if (alreadyRunning) return;
      void deleteNotificationsForSession(
        client,
        deleteInboxNotification,
        flowSessionId,
      );
    },
    [client, deleteInboxNotification],
  );

  return useMemo(() => ({ deleteForSession }), [deleteForSession]);
}

// Walk at most this many kind-filtered pages (50 each, most-recent-first) for
// the session's notification. It's near the top, so page one almost always
// hits; this only bounds a pathological unresolved backlog (~250 notifications).
const MAX_DELETE_PAGES = 5;

// Fetch the $toolConfirmation notifications for the session and delete the ones
// that predate the delete request (notifiedAt <= requestedAt); returns whether
// any matched. Gating on the request time is what stops a retry from removing a
// *newer*, legitimate nudge for the same chat that arrives during the retry
// window — that one has notifiedAt > requestedAt and is spared. There's one
// collapsed notification per session, so we stop as soon as we find it and only
// page into older notifications when it isn't on the most-recent page.
async function deleteHydratedForSession(
  client: LiveblocksClient,
  deleteInboxNotification: DeleteInboxNotificationFn,
  flowSessionId: string,
  requestedAt: number,
): Promise<boolean> {
  let deleted = false;
  let cursor: string | undefined;
  for (let page = 0; page < MAX_DELETE_PAGES; page++) {
    const { inboxNotifications, nextCursor } =
      await client.getInboxNotifications({
        query: { kind: "$toolConfirmation" },
        cursor,
      });
    for (const notification of inboxNotifications) {
      if (notification.kind !== "$toolConfirmation") continue;
      if (notification.notifiedAt.getTime() > requestedAt) continue;
      const matchesSession = notification.activities.some(
        (activity) =>
          "flow_session_id" in activity.data &&
          activity.data.flow_session_id === flowSessionId,
      );
      if (matchesSession) {
        deleteInboxNotification(notification.id);
        deleted = true;
      }
    }
    // One notification per session — stop once found; otherwise page older.
    if (deleted || !nextCursor) break;
    cursor = nextCursor;
  }
  return deleted;
}

async function deleteNotificationsForSession(
  client: LiveblocksClient,
  deleteInboxNotification: DeleteInboxNotificationFn,
  flowSessionId: string,
): Promise<void> {
  let servicedAt: number | undefined;
  try {
    let elapsedMs = 0;
    for (const offsetMs of RETRY_OFFSETS_MS) {
      const gapMs = offsetMs - elapsedMs;
      if (gapMs > 0) await sleep(gapMs);
      elapsedMs = offsetMs;
      const requestedAt = pendingDeletionsBySession.get(flowSessionId);
      if (requestedAt === undefined) return;
      servicedAt = requestedAt;
      try {
        const deleted = await deleteHydratedForSession(
          client,
          deleteInboxNotification,
          flowSessionId,
          requestedAt,
        );
        // Only stop if no newer request arrived while this attempt ran —
        // a newer one may target a notification this pass deliberately spared.
        if (
          deleted &&
          pendingDeletionsBySession.get(flowSessionId) === requestedAt
        ) {
          return;
        }
      } catch {
        // Fetch or delete failed; the next scheduled attempt retries.
      }
    }
  } finally {
    // If a newer request landed after our last attempt started, it never got its
    // own retry schedule — hand off to a fresh loop instead of dropping it.
    const latest = pendingDeletionsBySession.get(flowSessionId);
    if (latest !== undefined && latest !== servicedAt) {
      void deleteNotificationsForSession(
        client,
        deleteInboxNotification,
        flowSessionId,
      );
    } else {
      pendingDeletionsBySession.delete(flowSessionId);
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
